import { useFormContext } from "react-hook-form";
import {
  ServiceType,
  ShipmentStopType,
  TimeWindowSetting,
} from "@api/graphql/generated/generated-types";
import moment from "moment-timezone";
import { findLastIndex } from "lodash";
import "moment-business-days"; // Import moment-business-days to extend moment
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";
import { useAuth } from "@src/common/hooks/useAuth";
import { plusBusinessMinutes } from "@src/common/utils/businessHours";

type UseServiceTypeAutoFillProps = {
  setSelectedDuration: React.Dispatch<React.SetStateAction<string>>;
  memoizedShipmentDate: Date;
};

export const useServiceTypeAutoFill = ({
  setSelectedDuration,
  memoizedShipmentDate,
}: UseServiceTypeAutoFillProps) => {
  const { setValue, watch } = useFormContext();
  const { courier } = useAuth();
  const watchStops = watch("stops");

  const showPickupCloseFields =
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.BothPickUpAndDropOffDueTime ||
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.AllTimeWindowSettings;

  // Function to handle fixed due time type
  const handleFixedDueTimeType = (selectedServiceType: ServiceType) => {
    const dueTimeInDaysAfter = selectedServiceType.dueTimeInDaysAfter;
    const dueTimeInBusinessDaysAfter =
      selectedServiceType.dueTimeInBusinessDaysAfter as number;
    const dueTimeSecondsSinceMidnight =
      selectedServiceType.dueTimeSecondsSinceMidnight;
    const deliveryRoutedDaysAfter = selectedServiceType.deliveryRoutedDaysAfter;

    let dueTimeBase = moment().startOf("day");
    if (dueTimeInDaysAfter != null) {
      dueTimeBase = dueTimeBase.add(dueTimeInDaysAfter, "days");
    } else {
      dueTimeBase = dueTimeBase.businessAdd(dueTimeInBusinessDaysAfter);
    }
    const dueTimeInCourierTimeZone = dueTimeBase
      .add(dueTimeSecondsSinceMidnight, "seconds");

    const stopIndex = findLastIndex(watchStops, (field: any) => {
      return field.type === ShipmentStopType.DropOff;
    });

    if (stopIndex >= 0) {
      setValue(
        `stops.${stopIndex}.timeWindow.close`,
        dueTimeInCourierTimeZone.unix().toString()
      );
    }

    const shipmentDate = memoizedShipmentDate;
    if (shipmentDate) {
      const shipmentDateMoment = moment(shipmentDate);
      const deliveryRoutedDayPlusShipmentDate = shipmentDateMoment.add(
        deliveryRoutedDaysAfter,
        "days"
      );
      setValue(
        `stops.${stopIndex}.stopDate`,
        momentToTimePickerDate(deliveryRoutedDayPlusShipmentDate)
      );
    }

    const effectiveDaysAfter = dueTimeInDaysAfter ?? dueTimeInBusinessDaysAfter;
    if (effectiveDaysAfter > 0) {
      setSelectedDuration("hasStopDates");
      setValue(
        `stops.${stopIndex}.deliverByDate`,
        momentToTimePickerDate(dueTimeInCourierTimeZone)
      );
    }

    if (effectiveDaysAfter === 0) {
      setSelectedDuration("noStopDates");
      setValue(`stops.${stopIndex}.deliverByDate`, null);
    }
  };

  // Function to handle relative to pickup due time type
  const handleRelativeToPickupDueTimeType = (
    selectedServiceType: ServiceType
  ) => {
    if (!selectedServiceType.deliveryTimeInMinutes) return;

    const pickUpStop = watchStops.find(
      (stop: any) => stop.type === ShipmentStopType.PickUp
    );
    const pickUpStopReadyTime = pickUpStop?.timeWindow?.open;
    const pickUpStopReadyDate = pickUpStopReadyTime
      ? new Date(parseInt(pickUpStopReadyTime) * 1000)
      : undefined;

    const start = pickUpStopReadyDate
      ? moment(pickUpStopReadyDate)
      : moment();
    const pickupDayStart = start.clone().startOf("day");

    const dueTime = selectedServiceType.businessHoursSchedule?.weekDailyList
      ?.length
      ? plusBusinessMinutes(
          start,
          selectedServiceType.deliveryTimeInMinutes,
          selectedServiceType.businessHoursSchedule
        )
      : start.clone().add(selectedServiceType.deliveryTimeInMinutes, "minutes");

    const stopIndex = findLastIndex(watchStops, (field: any) => {
      return field.type === ShipmentStopType.DropOff;
    });

    if (stopIndex >= 0) {
      setValue(`stops.${stopIndex}.timeWindow.close`, dueTime.unix());

      if (dueTime.clone().startOf("day").isAfter(pickupDayStart)) {
        setSelectedDuration("hasStopDates");
        setValue(
          `stops.${stopIndex}.deliverByDate`,
          momentToTimePickerDate(dueTime)
        );
      } else {
        setSelectedDuration("noStopDates");
        setValue(`stops.${stopIndex}.deliverByDate`, null);
      }
    }
  };

  // Function to handle fixed pickup close time type
  const handleFixedPickupCloseTimeType = (selectedServiceType: ServiceType) => {
    if (!showPickupCloseFields) return;

    const pickupCloseInDaysAfter = selectedServiceType.pickupCloseTimeInDaysAfter;
    const pickupCloseInBusinessDaysAfter =
      selectedServiceType.pickupCloseTimeInBusinessDaysAfter ?? 0;
    const pickupCloseSecondsSinceMidnight =
      selectedServiceType.pickupCloseTimeInSecondsSinceMidnight;

    if (
      pickupCloseSecondsSinceMidnight === null ||
      pickupCloseSecondsSinceMidnight === undefined
    )
      return;

    const pickUpStopIndex = watchStops.findIndex(
      (stop: any) => stop.type === ShipmentStopType.PickUp
    );
    if (pickUpStopIndex < 0) return;

    let pickupCloseBase = moment().startOf("day");
    if (pickupCloseInDaysAfter != null) {
      pickupCloseBase = pickupCloseBase.add(pickupCloseInDaysAfter, "days");
    } else {
      pickupCloseBase = pickupCloseBase.businessAdd(pickupCloseInBusinessDaysAfter, "days");
    }
    const pickupCloseTimeInCourierTimeZone = pickupCloseBase
      .add(pickupCloseSecondsSinceMidnight, "seconds");

    setValue(
      `stops.${pickUpStopIndex}.timeWindow.close`,
      pickupCloseTimeInCourierTimeZone.unix().toString()
    );

    // Check if pickup close is in the future - use same pattern as delivery
    const effectivePickupCloseDaysAfter = pickupCloseInDaysAfter ?? pickupCloseInBusinessDaysAfter;
    if (effectivePickupCloseDaysAfter > 0) {
      // Pickup close is in the future - need multi-day mode
      setSelectedDuration("hasStopDates");
      setValue(
        `stops.${pickUpStopIndex}.pickupByDate`,
        momentToTimePickerDate(pickupCloseTimeInCourierTimeZone)
      );
    }
  };

  // Function to handle relative to pickup close time type
  const handleRelativeToPickupCloseTimeType = (
    selectedServiceType: ServiceType
  ) => {
    if (!showPickupCloseFields) return;
    if (!selectedServiceType.pickupCloseTimeInMinutes) return;

    const pickUpStopIndex = watchStops.findIndex(
      (stop: any) => stop.type === ShipmentStopType.PickUp
    );
    if (pickUpStopIndex < 0) return;

    const pickUpStop = watchStops[pickUpStopIndex];
    const pickUpStopReadyTime = pickUpStop?.timeWindow?.open;

    if (!pickUpStopReadyTime) return;

    // Convert Unix timestamp to moment and add minutes
    const pickupStart = moment.unix(parseInt(pickUpStopReadyTime));
    const pickupCloseTime = selectedServiceType.businessHoursSchedule
      ?.weekDailyList?.length
      ? plusBusinessMinutes(
          pickupStart,
          selectedServiceType.pickupCloseTimeInMinutes!,
          selectedServiceType.businessHoursSchedule
        )
      : pickupStart.add(
          selectedServiceType.pickupCloseTimeInMinutes!,
          "minutes"
        );

    setValue(
      `stops.${pickUpStopIndex}.timeWindow.close`,
      pickupCloseTime.unix().toString()
    );

    // Check if pickup close time ends up in a future day
    const startOfToday = moment().startOf("day");
    const pickupCloseDateStartOfDay = pickupCloseTime.clone().startOf("day");

    if (pickupCloseDateStartOfDay.isAfter(startOfToday)) {
      // Pickup close is in the future - need multi-day mode
      setSelectedDuration("hasStopDates");
      setValue(
        `stops.${pickUpStopIndex}.pickupByDate`,
        momentToTimePickerDate(pickupCloseTime)
      );
    }
  };

  return {
    handleFixedDueTimeType,
    handleRelativeToPickupDueTimeType,
    handleFixedPickupCloseTimeType,
    handleRelativeToPickupCloseTimeType,
    showPickupCloseFields,
  };
};
